import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Flame, Trophy, Star, Zap } from "lucide-react";

interface MilestoneCelebrationProps {
  milestone: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getMilestoneInfo = (days: number) => {
  switch (days) {
    case 7:
      return {
        title: "Week Warrior! 🔥",
        description: "7 days strong! You're building a powerful habit.",
        icon: Flame,
        color: "hsl(25 95% 53%)",
        badge: "Week Streak"
      };
    case 30:
      return {
        title: "Monthly Master! 💪",
        description: "30 days of dedication! You're unstoppable!",
        icon: Trophy,
        color: "hsl(280 100% 70%)",
        badge: "Month Streak"
      };
    case 100:
      return {
        title: "Century Champion! 🏆",
        description: "100 DAYS! You're a legend in the community!",
        icon: Star,
        color: "hsl(48 100% 50%)",
        badge: "Century Club"
      };
    default:
      return {
        title: "Streak Milestone! ⚡",
        description: `${days} days of consistency! Keep going!`,
        icon: Zap,
        color: "hsl(262 83% 58%)",
        badge: `${days} Days`
      };
  }
};

export const MilestoneCelebration = ({ milestone, open, onOpenChange }: MilestoneCelebrationProps) => {
  const [show, setShow] = useState(false);
  const info = getMilestoneInfo(milestone);
  const Icon = info.icon;

  useEffect(() => {
    if (open) {
      setShow(true);
      
      // Trigger multiple confetti bursts
      const duration = 3000;
      const end = Date.now() + duration;

      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'];

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: colors,
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      // Start confetti
      frame();

      // Big burst at the center
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: colors,
        });
      }, 200);

      // Auto close after 5 seconds
      const timer = setTimeout(() => {
        setShow(false);
        setTimeout(() => onOpenChange(false), 300);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [open, onOpenChange]);

  return (
    <Dialog open={show} onOpenChange={(open) => {
      setShow(open);
      if (!open) {
        setTimeout(() => onOpenChange(false), 300);
      }
    }}>
      <DialogContent className="sm:max-w-md border-none bg-gradient-to-br from-background via-background to-primary/5 animate-scale-in">
        <div className="flex flex-col items-center text-center space-y-6 py-6">
          {/* Animated Icon */}
          <div className="relative">
            <div 
              className="absolute inset-0 rounded-full blur-2xl opacity-50 animate-pulse"
              style={{ backgroundColor: info.color }}
            />
            <div 
              className="relative w-24 h-24 rounded-full flex items-center justify-center animate-[spin_3s_ease-in-out_infinite]"
              style={{ 
                background: `linear-gradient(135deg, ${info.color}, ${info.color}dd)`,
                boxShadow: `0 0 40px ${info.color}80`
              }}
            >
              <Icon className="w-12 h-12 text-white animate-pulse" />
            </div>
          </div>

          {/* Badge */}
          <Badge 
            className="text-lg px-6 py-2 animate-bounce"
            style={{ 
              backgroundColor: info.color,
              color: 'white',
              border: 'none'
            }}
          >
            {info.badge}
          </Badge>

          {/* Title */}
          <div className="space-y-2 animate-fade-in">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              {info.title}
            </h2>
            <p className="text-muted-foreground text-base max-w-sm">
              {info.description}
            </p>
          </div>

          {/* Streak Counter */}
          <div className="flex items-center gap-2 bg-card/50 backdrop-blur-sm rounded-full px-6 py-3 animate-scale-in">
            <Flame className="w-6 h-6 text-orange-500 animate-pulse" />
            <span className="text-4xl font-bold text-foreground">{milestone}</span>
            <span className="text-sm text-muted-foreground">days</span>
          </div>

          {/* Motivational Message */}
          <p className="text-sm text-muted-foreground italic animate-fade-in">
            "Consistency is the bridge between goals and accomplishment."
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
